import React, { useState, useEffect } from 'react';
import { Button, Modal, Toast } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useAuth } from 'app/context/auth-context';
import { VJFPUserService } from 'app/services/VJFPUserService';
import { VJFPUser, Booth } from 'app/API';
import { useS3Object } from 'app/context/S3ObjectContext';
import { getStyleByIndex } from 'app/helpers/MiscUtils';
import { useAppContext } from 'app/context/app-context';
import { BoothService } from 'app/services/BoothService';

// Add this helper function at the top of the file
const generateStrongPassword = () => {
    const length = 12;
    const numbers = "0123456789";
    const special = "!@#$%^&*";
    const letters = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

    // Ensure at least one of each required type
    let password = numbers.charAt(Math.floor(Math.random() * numbers.length)) +
        special.charAt(Math.floor(Math.random() * special.length));

    // Fill the rest with random characters from all sets
    const charset = letters + numbers + special;
    for (let i = password.length; i < length; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    // Shuffle the password to avoid predictable positions
    return password.split('').sort(() => Math.random() - 0.5).join('');
};

const formatPhoneNumber = (value: string) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const ManageUsers: React.FC<{ organizationId: string }> = ({ organizationId }: { organizationId: string }) => {
    const [users, setUsers] = useState<VJFPUser[]>([]);
    const [showModal, setShowModal] = useState<boolean>(false);
    const { user: currentUser } = useAuth();
    const vjfpUserService = new VJFPUserService();
    const [selectedUser, setSelectedUser] = useState<VJFPUser | null>(null);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [userToDelete, setUserToDelete] = useState<VJFPUser | null>(null);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [formError, setFormError] = useState<string>('');
    const { getS3ObjectUrl, s3ObjectCache } = useS3Object();
    const { jobFair } = useAppContext();
    const [booths, setBooths] = useState<Booth[]>([]);
    const boothService = new BoothService();

    const fetchUsers = async () => {
        if (currentUser?.id) {
            const fetchedUsers = await vjfpUserService.getUsers(currentUser.organizationId);
            fetchedUsers.forEach(user => {
                if (user.avatar) {
                    getS3ObjectUrl(user.avatar);
                }
            });
            setUsers(fetchedUsers);
        }
    };

    const fetchBooths = async () => {
        if (jobFair?.id) {
            const fetchedBooths = await boothService.getAllBooths(jobFair.id);
            // Sort by createdAt or id to find the last created booth
            const sortedBooths = [...fetchedBooths].sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return dateB - dateA; // Newest first
            });
            setBooths(sortedBooths);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchBooths();
    }, [jobFair?.id]);

    // Update modal open handler
    const handleModalOpen = (user?: VJFPUser) => {
        if (user) {
            setSelectedUser(user);
            setModalMode('edit');
            formik.setValues({
                boothId: '',
                firstName: user.firstName,
                lastName: user.lastName || '',
                email: user.emailId,
                phone_number: user.phone_number || '',
                tempPassword: ''
            });
        } else {
            setSelectedUser(null);
            setModalMode('create');
            formik.resetForm();
            if (booths.length > 0) {
                // By default last created booth should be tagged automatically
                formik.setFieldValue('boothId', booths[0].id);
            }
        }
        setShowModal(true);
    };


    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone_number: '',
            tempPassword: generateStrongPassword(),
            boothId: '',
        },
        validationSchema: Yup.object({
            firstName: Yup.string().required('First Name is required'),
            lastName: Yup.string().required('Last Name is required'),
            email: Yup.string().email('Invalid email address').required('Email is required'),
            phone_number: Yup.string().required('Phone number is required')
                .matches(/^[+][1][0-9]{10}$/, 'Phone number must be in format +19999999999'),
            tempPassword: modalMode === 'create'
                ? Yup.string().required('Temporary Password is required')
                : Yup.string(),
            boothId: Yup.string(),
        }),
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            if (!currentUser?.id) return;
            setFormError('');
            setSubmitting(true);

            try {
                if (modalMode === 'create') {
                    const newUserId = await vjfpUserService.createOrgUser(organizationId, {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        emailId: values.email.toLowerCase(),
                        organizationId: currentUser.organizationId,
                        phone_number: values.phone_number,
                        roles: ['Recruiter'],
                    }, values.tempPassword);

                    if (values.boothId && jobFair?.id) {
                        const booth = await boothService.getBoothById(jobFair.id, values.boothId);
                        if (booth) {
                            const recruiters = booth.recruiters ? [...booth.recruiters] : [];
                            if (!recruiters.includes(newUserId)) {
                                recruiters.push(newUserId);
                                await boothService.updateBooth(jobFair.id, values.boothId, {
                                    recruiters: recruiters as number[]
                                });
                            }
                        }
                    }
                } else if (selectedUser) {
                    await vjfpUserService.updateProfile(organizationId, selectedUser.id, {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        emailId: values.email.toLowerCase(),
                        phone_number: values.phone_number,
                        ...(values.tempPassword ? { tempPassword: values.tempPassword } : {})
                    });
                    // Booth assignment is intentionally handled only during user creation.
                }

                setShowModal(false);
                resetForm();
                fetchUsers();
            } catch (error) {
                console.error('Error managing user:', error);
                const errorCode = (error as any).code;
                let errorMessage = 'An error occurred while managing the user.';

                switch (errorCode) {
                    case 'VALIDATION_ERROR':
                        errorMessage = 'Please enter a valid email address.';
                        break;
                    case 'DUPLICATE_EMAIL':
                        errorMessage = 'This email is already in use.';
                        break;
                    case 'API_ERROR':
                    default:
                        errorMessage = (error as Error).message || 'Failed to create/update user. Please try again.';
                        break;
                }

                setFormError(errorMessage);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const rawValue = value.replace(/[^\d]/g, '');
        // If the user pasted a number starting with 1, we strip it because we prepend +1
        const cleanValue = rawValue.startsWith('1') && rawValue.length > 10 ? rawValue.slice(1) : rawValue;
        const limitedValue = cleanValue.slice(0, 10);
        formik.setFieldValue('phone_number', limitedValue ? `+1${limitedValue}` : '');
    };

    const handleDeleteUser = (user: VJFPUser) => {
        if (user.id === currentUser?.id) {
            alert("You cannot delete your own account");
            return;
        }
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        setLoading(true);

        try {
            await vjfpUserService.deleteUserById(organizationId, userToDelete.id);
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
        }
        finally {
            setLoading(false);
        }
        setShowDeleteModal(false);
    };

    return (
        <div className="card">
            <div className="card-header border-0 pt-6">
                <div className="card-title">
                    <div className="d-flex align-items-center position-relative my-1">
                        <i className="ki-duotone ki-magnifier fs-1 position-absolute ms-6">
                            <span className="path1"></span>
                            <span className="path2"></span>
                        </i>
                        <input
                            type="text"
                            data-kt-user-table-filter="search"
                            className="form-control form-control-solid w-250px ps-14"
                            placeholder="Search user"
                        />
                    </div>
                </div>
                <div className="card-toolbar">
                    <div className="d-flex justify-content-end" data-kt-user-table-toolbar="base">
                        <Button onClick={() => handleModalOpen()}>Add recruiter</Button>
                    </div>
                </div>
            </div>
            <div className="card-body py-4">
                {users?.length ? (
                    <div className="table-responsive">
                        <table className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
                            <thead>
                                <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                    <th className="min-w-250px">Full Name</th>
                                    <th className="min-w-150px">Contact info</th>
                                    <th className="min-w-125px">Role</th>
                                    <th className="text-end min-w-100px">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="fw-semibold text-gray-600">
                                {users.map((user, index) => {
                                    const initials = `${user.firstName?.charAt(0)}${user.lastName?.charAt(0)}`;
                                    const profilePic = user.avatar ? s3ObjectCache[user.avatar] : null;

                                    return (
                                        <tr key={user.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="symbol symbol-35px me-3">
                                                        {profilePic ? (
                                                            <img src={profilePic} alt={initials} crossOrigin="anonymous" style={{ objectFit: 'cover' }} />
                                                        ) : (
                                                            <div className={`symbol-label fs-7 fw-bold bg-${getStyleByIndex(index)} text-inverse-${getStyleByIndex(index)}`}>
                                                                {initials}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="d-flex flex-column">
                                                        <span className="text-gray-800 fw-bold">{user.firstName} {user.lastName}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="align-middle">
                                                <div className="d-flex flex-column justify-content-center">
                                                    <a href={`mailto:${user.emailId}`} className="text-gray-600 text-hover-primary fs-7 mb-0">
                                                        {user.emailId}
                                                    </a>
                                                    {user.phone_number && (
                                                        <span className="text-gray-500 fs-7 mt-2">{user.phone_number}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="align-middle">
                                                <span className="badge badge-light-primary">{user.roles?.join(', ')}</span>
                                            </td>
                                            <td className="text-end">
                                                <button
                                                    className="btn btn-icon btn-light-primary btn-active-light-primary btn-sm me-2"
                                                    onClick={() => handleModalOpen(user)}
                                                >
                                                    <i className="bi bi-pencil fs-4"></i>
                                                </button>

                                                <button
                                                    className="btn btn-icon btn-light-danger btn-active-light-danger btn-sm"
                                                    onClick={() => handleDeleteUser(user)}
                                                >
                                                    <i className="bi bi-trash fs-4"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="card-px text-center py-20 my-10">
                        <h2 className="fs-2x fw-bold mb-10">No Recruiters Added Yet</h2>
                        <p className="text-gray-400 fs-4 fw-semibold mb-10">
                            Onboard Recruiters to your organisation
                        </p>
                        <Button variant="primary" onClick={() => handleModalOpen()}>
                            Add Recruiter(s)
                        </Button>
                    </div>
                )}
            </div>
            <Modal show={showModal} onHide={() => { setShowModal(false); setFormError(''); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{modalMode === 'create' ? 'Add New recruiter' : 'Edit recruiter'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && (
                        <div className="alert alert-danger" role="alert">
                            <div className="alert-icon"><i className="bi bi-exclamation-triangle"></i></div>
                            <div className="alert-text">{formError}</div>
                            <div className="alert-close">
                                <button type="button" className="btn-close" onClick={() => setFormError('')}></button>
                            </div>
                        </div>
                    )}
                    <form onSubmit={formik.handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="firstName" className="form-label">First Name</label>
                            <input
                                id="firstName"
                                name="firstName"
                                type="text"
                                className={`form-control ${formik.touched.firstName && formik.errors.firstName ? 'is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.firstName}
                            />
                            {formik.touched.firstName && formik.errors.firstName && (
                                <div className="invalid-feedback">{formik.errors.firstName}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="lastName" className="form-label">Last Name</label>
                            <input
                                id="lastName"
                                name="lastName"
                                type="text"
                                className={`form-control ${formik.touched.lastName && formik.errors.lastName ? 'is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.lastName}
                            />
                            {formik.touched.lastName && formik.errors.lastName && (
                                <div className="invalid-feedback">{formik.errors.lastName}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="email" className="form-label">Email</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                            />
                            {formik.touched.email && formik.errors.email && (
                                <div className="invalid-feedback">{formik.errors.email}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="phone_number" className="form-label">Mobile Phone</label>
                            <div className="input-group">
                                <span className="input-group-text text-muted" style={{ backgroundColor: '#f3f6f9' }}>+1</span>
                                <input
                                    id="phone_number"
                                    name="phone_number"
                                    type="text"
                                    className={`form-control ${formik.touched.phone_number && formik.errors.phone_number ? 'is-invalid' : ''}`}
                                    placeholder="(xxx) xxx-xxxx"
                                    value={formatPhoneNumber(formik.values.phone_number?.replace('+1', '') || '')}
                                    onChange={handlePhoneChange}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.touched.phone_number && formik.errors.phone_number && (
                                    <div className="invalid-feedback d-block">{formik.errors.phone_number}</div>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 position-relative">
                            <label htmlFor="tempPassword" className="form-label">{modalMode === 'create' ? 'Temporary Password' : 'New Temporary Password'}</label>
                            <div className="input-group">
                                <input
                                    id="tempPassword"
                                    name="tempPassword"
                                    type={showPassword ? "text" : "password"}
                                    className="form-control"
                                    value={formik.values.tempPassword}
                                    readOnly
                                />
                                <button
                                    className="btn btn-secondary"
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    type="button"
                                    onClick={() => {
                                        navigator.clipboard.writeText(formik.values.tempPassword);
                                        setShowToast(true);
                                    }}
                                >
                                    <i className="bi bi-clipboard"></i>
                                </button>
                            </div>
                            <small className="text-muted">
                                {modalMode === 'create' ? "This is a generated strong password for the new user's initial login" : "This generates and applies a new temporary password for the user on save"}
                            </small>
                        </div>

                        {modalMode === 'create' && (
                            <div className="mb-3">
                                <label htmlFor="boothId" className="form-label">Assign Booth</label>
                                <select
                                    id="boothId"
                                    name="boothId"
                                    className={`form-select ${formik.touched.boothId && formik.errors.boothId ? 'is-invalid' : ''}`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.boothId}
                                >
                                    <option value="">Select a booth</option>
                                    {booths.map((booth) => (
                                        <option key={booth.id} value={booth.id}>
                                            {booth.name}
                                        </option>
                                    ))}
                                </select>
                                {formik.touched.boothId && formik.errors.boothId && (
                                    <div className="invalid-feedback">{formik.errors.boothId}</div>
                                )}
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">Role</label>
                            <input
                                type="text"
                                className="form-control"
                                value="Recruiter"
                                disabled
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={formik.isSubmitting}
                        >
                            {formik.isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Processing...
                                </>
                            ) : (
                                modalMode === 'create' ? 'Create User' : 'Update User'
                            )}
                        </button>
                    </form>
                </Modal.Body>
            </Modal>
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>

                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {userToDelete && (
                        <div>
                            <p className="fw-bold text-gray-800 fs-6">
                                This will permanently delete {userToDelete.firstName} {userToDelete.lastName}. This action cannot be undone.
                            </p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={confirmDelete}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Processing...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </Button></>
                </Modal.Footer>
            </Modal>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                style={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    zIndex: 9999
                }}
                delay={3000}
                autohide={true}
            >
                <Toast.Header>
                    <strong className="me-auto">Success</strong>
                </Toast.Header>
                <Toast.Body>Password copied to clipboard!</Toast.Body>
            </Toast>
        </div>
    );
};

export default ManageUsers;
